import { Status, SVLabeledTextProps } from '@/types/common';
import {
  Avatar,
  Box,
  Card,
  Divider,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useState } from 'react';
import SVButton from '../SVButton';
import SVLabeledText from '../SVLabeledText';
import StatusLabel from '../../StatusLabel';
import { BankAccountStatus } from '@/types/bankAccounts';
import BankAccountStatusLabel from '@/components/BankAccountStatusLabel';
import { BankApplicationStatus } from '@/types/bankApplications';
import BankApplicationStatusLabel from '@/components/BankApplicationStatusLabel';

type ManageMenuItem = {
  label: string;
  onClick: () => void;
};

type SVListItemCardProps = {
  onClick: () => void;
  header: string;
  manageMenuItems: ManageMenuItem[];
  additionalInfo: SVLabeledTextProps[];
  disabled?: boolean;
  loading?: boolean;
} & (
  | {
      showAvatar: true;
      avatar: string | null;
    }
  | {
      showAvatar?: false;
      avatar?: never;
    }
) &
  (
    | {
        statusType: 'bankAccount';
        status: BankAccountStatus;
      }
    | {
        statusType: 'standard';
        status: Status;
      }
    | {
        statusType: 'bankAccountApplication';
        status: BankApplicationStatus;
      }
    | {
        statusType?: never;
        status?: never;
      }
  );

export default function SVListItemCard({
  onClick,
  avatar,
  showAvatar = false,
  header,
  status,
  statusType,
  manageMenuItems,
  additionalInfo,
}: SVListItemCardProps) {
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const isStandardStatus = statusType === 'standard';
  const isBankAccountStatus = statusType === 'bankAccount';
  const isBankAccountApplicationStatus =
    statusType === 'bankAccountApplication';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const ariaLabel = `${header} Details. ${additionalInfo.map(item => `${item.label} ${item.text}.`).join(' ')}`;

  return (
    <Card data-testid="list-item-card" aria-label={ariaLabel} variant="hover">
      <Box
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column',
          justifyContent: 'space-between',
          alignItems: isLargeScreen ? 'center' : 'flex-start',
        }}>
        <Box
          onClick={onClick}
          sx={{ display: 'flex', flexDirection: 'row', gap: 1, flex: 1 }}>
          {showAvatar && (
            <Avatar
              data-testid="list-item-avatar"
              sx={{ height: 32, width: 32 }}
              src={avatar || ''}
            />
          )}
          <Typography fontWeight="bold">{header}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: isLargeScreen ? 'auto' : '100%',
            gap: 2,
          }}>
          {status !== undefined && (
            <>
              {isBankAccountStatus && (
                <BankAccountStatusLabel isSmall status={status} />
              )}
              {isBankAccountApplicationStatus && (
                <BankApplicationStatusLabel status={status} />
              )}
              {isStandardStatus && <StatusLabel isSmall status={status} />}
            </>
          )}
          <SVButton variant="text" onClick={handleClick}>
            Manage
          </SVButton>
          <Menu
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleClose}
            slotProps={{
              paper: {
                style: {
                  minWidth: 150,
                },
              },
            }}>
            {manageMenuItems.map(item => (
              <MenuItem key={item.label} onClick={item.onClick}>
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <Divider onClick={onClick} sx={{ mt: 1, mb: 1 }} />
      <Box
        onClick={onClick}
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column',
          gap: 2,
        }}>
        {additionalInfo.map(info => (
          <Box key={info.label} sx={{ flex: 1 }}>
            <SVLabeledText
              label={info.label}
              text={info.text}
              isPrimaryColor
              isSmall
            />
          </Box>
        ))}
      </Box>
    </Card>
  );
}
